import * as PIXI from 'pixi.js';
import { Tower, Wall } from '../types/GameTypes';
import { UPGRADE_A_STATS, UPGRADE_B_STATS, UPGRADE_C_STATS, getUpgradeCost, TOWER_STATS } from '../types/TowerTypes';
import { GAME_CONSTANTS } from '../utils/Constants';
import { RepairSystem } from '../systems/RepairSystem';

export class TowerInfoPanel {
  private container: PIXI.Container;
  private tower: Tower | null = null;
  private wall: Wall | null = null;
  private repairSystem?: RepairSystem;
  
  // Background elements
  private backgroundPanel!: PIXI.Graphics;
  private headerBackground!: PIXI.Graphics;
  private statsBackground!: PIXI.Graphics;
  private actionsBackground!: PIXI.Graphics;
  
  // Text elements
  private nameText!: PIXI.Text;
  private levelText!: PIXI.Text;
  private statsText!: PIXI.Text;
  private hpBarBackground!: PIXI.Graphics;
  private hpBarFill!: PIXI.Graphics;
  private hpText!: PIXI.Text;
  
  // Upgrade buttons
  private upgradeAButton!: PIXI.Graphics;
  private upgradeAText!: PIXI.Text;
  private upgradeBButton!: PIXI.Graphics;
  private upgradeBText!: PIXI.Text;
  private upgradeCButton!: PIXI.Graphics;
  private upgradeCText!: PIXI.Text;
  
  // Action buttons
  private sellButton!: PIXI.Graphics;
  private sellText!: PIXI.Text;
  private repairButton!: PIXI.Graphics;
  private repairText!: PIXI.Text;
  
  // Callbacks
  public onUpgradeCallback?: (tower: Tower, path: 'A' | 'B' | 'C') => void;
  public onSellCallback?: (tower: Tower | Wall) => void;
  public onRepairCallback?: (tower: Tower | Wall) => void;
  public onErrorCallback?: (message: string) => void;
  
  // State
  private upgradeAState: 'available' | 'unavailable' | 'disabled' | 'locked' = 'available';
  private upgradeBState: 'available' | 'unavailable' | 'disabled' | 'locked' = 'available';
  private upgradeCState: 'available' | 'unavailable' | 'disabled' | 'locked' = 'available';
  
  // Panel dimensions (reduced for compactness)
  private readonly PANEL_WIDTH = 520;
  private readonly PANEL_HEIGHT = 400;
  private readonly HEADER_HEIGHT = 50;
  private readonly STATS_HEIGHT = 140;
  private readonly ACTIONS_HEIGHT = 90;
  private readonly BUTTON_HEIGHT = 45;
  private readonly BUTTON_SPACING = 8;

  constructor() {
    this.container = new PIXI.Container();
    // Position on right side, below WaveInfoPanel (which is at y=280)
    // WaveInfoPanel can expand, so position this at y=500 to give room
    // Align with other right-side panels (x=1320)
    this.container.x = 1320;
    this.container.y = 500;
    this.container.visible = false;
    this.setupPanel();
  }

  private setupPanel(): void {
    // Main background panel with rounded corners effect
    this.backgroundPanel = new PIXI.Graphics();
    this.backgroundPanel.beginFill(0x1a1a1a, 0.95); // Dark semi-transparent background
    this.backgroundPanel.drawRect(0, 0, this.PANEL_WIDTH, this.PANEL_HEIGHT);
    this.backgroundPanel.endFill();
    this.backgroundPanel.lineStyle(2, 0x444444, 0.8);
    this.backgroundPanel.drawRect(0, 0, this.PANEL_WIDTH, this.PANEL_HEIGHT);
    this.container.addChild(this.backgroundPanel);

    // Header section
    this.headerBackground = new PIXI.Graphics();
    this.headerBackground.beginFill(0x2a2a2a, 0.9);
    this.headerBackground.drawRect(0, 0, this.PANEL_WIDTH, this.HEADER_HEIGHT);
    this.headerBackground.endFill();
    this.headerBackground.lineStyle(1, 0x555555, 0.6);
    this.headerBackground.drawRect(0, 0, this.PANEL_WIDTH, this.HEADER_HEIGHT);
    this.container.addChild(this.headerBackground);

    // Tower name
    this.nameText = new PIXI.Text('', {
      fontFamily: 'Courier New',
      fontSize: 28,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    this.nameText.x = 12;
    this.nameText.y = 6;
    this.container.addChild(this.nameText);

    // Level badge
    this.levelText = new PIXI.Text('', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xffd700,
      fontWeight: 'bold',
    });
    this.levelText.x = this.PANEL_WIDTH - 80;
    this.levelText.y = 16;
    this.container.addChild(this.levelText);

    // Stats section background
    this.statsBackground = new PIXI.Graphics();
    this.statsBackground.beginFill(0x151515, 0.7);
    this.statsBackground.drawRect(0, this.HEADER_HEIGHT, this.PANEL_WIDTH, this.STATS_HEIGHT);
    this.statsBackground.endFill();
    this.container.addChild(this.statsBackground);

    // HP Bar background
    this.hpBarBackground = new PIXI.Graphics();
    this.hpBarBackground.beginFill(0x330000, 0.8);
    this.hpBarBackground.drawRect(12, this.HEADER_HEIGHT + 10, this.PANEL_WIDTH - 24, 20);
    this.hpBarBackground.endFill();
    this.hpBarBackground.lineStyle(1, 0x666666, 0.6);
    this.hpBarBackground.drawRect(12, this.HEADER_HEIGHT + 10, this.PANEL_WIDTH - 24, 20);
    this.container.addChild(this.hpBarBackground);

    // HP Bar fill
    this.hpBarFill = new PIXI.Graphics();
    this.container.addChild(this.hpBarFill);

    // HP Text
    this.hpText = new PIXI.Text('', {
      fontFamily: 'Courier New',
      fontSize: 14,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    this.hpText.x = this.PANEL_WIDTH / 2;
    this.hpText.y = this.HEADER_HEIGHT + 12;
    this.hpText.anchor.set(0.5, 0);
    this.container.addChild(this.hpText);

    // Stats text (more compact)
    this.statsText = new PIXI.Text('', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xcccccc,
      wordWrap: true,
      wordWrapWidth: this.PANEL_WIDTH - 24,
    });
    this.statsText.x = 12;
    this.statsText.y = this.HEADER_HEIGHT + 35;
    this.container.addChild(this.statsText);

    // Actions section background
    this.actionsBackground = new PIXI.Graphics();
    this.actionsBackground.beginFill(0x1a1a1a, 0.8);
    this.actionsBackground.drawRect(0, this.HEADER_HEIGHT + this.STATS_HEIGHT, this.PANEL_WIDTH, this.ACTIONS_HEIGHT);
    this.actionsBackground.endFill();
    this.actionsBackground.lineStyle(1, 0x444444, 0.6);
    this.actionsBackground.drawRect(0, this.HEADER_HEIGHT + this.STATS_HEIGHT, this.PANEL_WIDTH, this.ACTIONS_HEIGHT);
    this.container.addChild(this.actionsBackground);

    // Upgrade buttons (horizontal layout)
    const upgradeButtonWidth = (this.PANEL_WIDTH - 24 - this.BUTTON_SPACING) / 2;
    const upgradeY = this.HEADER_HEIGHT + this.STATS_HEIGHT + 8;

    // Upgrade A button
    this.upgradeAButton = new PIXI.Graphics();
    this.upgradeAButton.x = 12;
    this.upgradeAButton.y = upgradeY;
    this.upgradeAButton.eventMode = 'static';
    this.upgradeAButton.cursor = 'pointer';
    this.upgradeAButton.on('pointerdown', (e) => {
      e.stopPropagation();
      if (this.tower) {
        if (this.upgradeAState === 'disabled' || this.upgradeAState === 'locked') {
          if (this.onErrorCallback) {
            const message = this.upgradeAState === 'disabled' 
              ? 'Upgrade already bought' 
              : 'Upgrade A is locked';
            this.onErrorCallback(message);
          }
          return;
        }
        if (this.upgradeAState === 'unavailable') {
          if (this.onErrorCallback) {
            this.onErrorCallback('Not enough gold for Upgrade A');
          }
          return;
        }
        if (this.onUpgradeCallback) {
        this.onUpgradeCallback(this.tower, 'A');
        }
      }
    });
    this.container.addChild(this.upgradeAButton);

    this.upgradeAText = new PIXI.Text('', {
      fontFamily: 'Courier New',
      fontSize: 14,
      fill: 0xffffff,
      align: 'center',
      wordWrap: true,
      wordWrapWidth: upgradeButtonWidth - 8,
    });
    this.upgradeAText.x = this.upgradeAButton.x + upgradeButtonWidth / 2;
    this.upgradeAText.y = this.upgradeAButton.y + 6;
    this.upgradeAText.anchor.set(0.5, 0);
    this.container.addChild(this.upgradeAText);

    // Upgrade B button
    this.upgradeBButton = new PIXI.Graphics();
    this.upgradeBButton.x = 12 + upgradeButtonWidth + this.BUTTON_SPACING;
    this.upgradeBButton.y = upgradeY;
    this.upgradeBButton.eventMode = 'static';
    this.upgradeBButton.cursor = 'pointer';
    this.upgradeBButton.on('pointerdown', (e) => {
      e.stopPropagation();
      if (this.tower) {
        if (this.upgradeBState === 'disabled' || this.upgradeBState === 'locked') {
          if (this.onErrorCallback) {
            const message = this.upgradeBState === 'disabled' 
              ? 'Upgrade already bought' 
              : 'Upgrade B requires Upgrade A first';
            this.onErrorCallback(message);
          }
          return;
        }
        if (this.upgradeBState === 'unavailable') {
          if (this.onErrorCallback) {
            this.onErrorCallback('Not enough gold for Upgrade B');
          }
          return;
        }
        if (this.onUpgradeCallback) {
        this.onUpgradeCallback(this.tower, 'B');
        }
      }
    });
    this.container.addChild(this.upgradeBButton);

    this.upgradeBText = new PIXI.Text('', {
      fontFamily: 'Courier New',
      fontSize: 14,
      fill: 0xffffff,
      align: 'center',
      wordWrap: true,
      wordWrapWidth: upgradeButtonWidth - 8,
    });
    this.upgradeBText.x = this.upgradeBButton.x + upgradeButtonWidth / 2;
    this.upgradeBText.y = this.upgradeBButton.y + 6;
    this.upgradeBText.anchor.set(0.5, 0);
    this.container.addChild(this.upgradeBText);

    // Upgrade C button (below Upgrade B)
    this.upgradeCButton = new PIXI.Graphics();
    this.upgradeCButton.x = 12;
    this.upgradeCButton.y = upgradeY + this.BUTTON_HEIGHT + this.BUTTON_SPACING;
    this.upgradeCButton.eventMode = 'static';
    this.upgradeCButton.cursor = 'pointer';
    this.upgradeCButton.on('pointerdown', (e) => {
      e.stopPropagation();
      if (this.tower) {
        if (this.upgradeCState === 'disabled' || this.upgradeCState === 'locked') {
          if (this.onErrorCallback) {
            const message = this.upgradeCState === 'disabled' 
              ? 'Upgrade already bought' 
              : 'Upgrade C requires Upgrade B first';
            this.onErrorCallback(message);
          }
          return;
        }
        if (this.upgradeCState === 'unavailable') {
          if (this.onErrorCallback) {
            this.onErrorCallback('Not enough gold for Upgrade C');
          }
          return;
        }
        if (this.onUpgradeCallback) {
          this.onUpgradeCallback(this.tower, 'C');
        }
      }
    });
    this.container.addChild(this.upgradeCButton);

    this.upgradeCText = new PIXI.Text('', {
      fontFamily: 'Courier New',
      fontSize: 14,
      fill: 0xffffff,
      align: 'center',
      wordWrap: true,
      wordWrapWidth: this.PANEL_WIDTH - 24,
    });
    this.upgradeCText.x = this.upgradeCButton.x + (this.PANEL_WIDTH - 24) / 2;
    this.upgradeCText.y = this.upgradeCButton.y + 6;
    this.upgradeCText.anchor.set(0.5, 0);
    this.container.addChild(this.upgradeCText);

    // Action buttons (Sell and Repair) - horizontal layout
    const actionButtonWidth = (this.PANEL_WIDTH - 24 - this.BUTTON_SPACING) / 2;
    const actionY = upgradeY + (this.BUTTON_HEIGHT + this.BUTTON_SPACING) * 2;

    // Sell button
    this.sellButton = new PIXI.Graphics();
    this.sellButton.x = 12;
    this.sellButton.y = actionY;
    this.sellButton.eventMode = 'static';
    this.sellButton.cursor = 'pointer';
    this.sellButton.on('pointerdown', () => {
      if (this.tower && this.onSellCallback) {
        this.onSellCallback(this.tower);
      } else if (this.wall && this.onSellCallback) {
        this.onSellCallback(this.wall);
      }
    });
    this.container.addChild(this.sellButton);

    this.sellText = new PIXI.Text('', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xffffff,
      fontWeight: 'bold',
      align: 'center',
    });
    this.sellText.x = this.sellButton.x + actionButtonWidth / 2;
    this.sellText.y = this.sellButton.y + this.BUTTON_HEIGHT / 2;
    this.sellText.anchor.set(0.5, 0.5);
    this.container.addChild(this.sellText);

    // Repair button
    this.repairButton = new PIXI.Graphics();
    this.repairButton.x = 12 + actionButtonWidth + this.BUTTON_SPACING;
    this.repairButton.y = actionY;
    this.repairButton.eventMode = 'static';
    this.repairButton.cursor = 'pointer';
    this.repairButton.on('pointerdown', () => {
      if (this.tower && this.onRepairCallback) {
        this.onRepairCallback(this.tower);
      } else if (this.wall && this.onRepairCallback) {
        this.onRepairCallback(this.wall);
      }
    });
    this.container.addChild(this.repairButton);

    this.repairText = new PIXI.Text('', {
      fontFamily: 'Courier New',
      fontSize: 14,
      fill: 0xffffff,
      fontWeight: 'bold',
      align: 'center',
      wordWrap: true,
      wordWrapWidth: actionButtonWidth - 8,
    });
    this.repairText.x = this.repairButton.x + actionButtonWidth / 2;
    this.repairText.y = this.repairButton.y + this.BUTTON_HEIGHT / 2;
    this.repairText.anchor.set(0.5, 0.5);
    this.container.addChild(this.repairText);
  }

  showTower(tower: Tower, canAffordA: boolean, canAffordB: boolean, canAffordC: boolean = false, isBuildMode: boolean = false): void {
    this.tower = tower;
    this.wall = null;
    this.container.visible = true;
    this.update(tower, canAffordA, canAffordB, canAffordC, isBuildMode);
  }

  showWall(canAfford: boolean, wall?: Wall): void {
    this.tower = null;
    this.wall = wall || null;
    this.container.visible = true;
    this.updateWall(canAfford, wall);
  }

  hide(): void {
    this.tower = null;
    this.wall = null;
    this.container.visible = false;
  }

  onUpgrade(callback: (tower: Tower, path: 'A' | 'B' | 'C') => void): void {
    this.onUpgradeCallback = callback;
  }

  onSell(callback: (item: Tower | Wall) => void): void {
    this.onSellCallback = callback;
  }

  onError(callback: (message: string) => void): void {
    this.onErrorCallback = callback;
  }

  onRepair(callback: (item: Tower | Wall) => void): void {
    this.onRepairCallback = callback;
  }

  setRepairSystem(repairSystem: RepairSystem): void {
    this.repairSystem = repairSystem;
  }

  private update(tower: Tower, canAffordA: boolean, canAffordB: boolean, canAffordC: boolean = false, isBuildMode: boolean = false): void {
    const upgradeA = UPGRADE_A_STATS[tower.type];
    const upgradeB = UPGRADE_B_STATS[tower.type];
    const upgradeC = (tower.type === 'arrow' || tower.type === 'cannon' || tower.type === 'sniper') 
      ? UPGRADE_C_STATS[tower.type] 
      : null;

    // Tower descriptions
    const towerDescriptions: Record<string, string> = {
      arrow: 'Single target physical',
      cannon: 'AOE Physical',
      sniper: 'Long range single target',
      fire: 'AOE Fire damage with burn',
      ice: 'AOE Ice damage with slow',
      lightning: 'Chain lightning damage',
    };

    // Header
    this.nameText.text = `${tower.type.toUpperCase()} TOWER`;
    this.levelText.text = `Level ${tower.level}`;

    // HP Bar
    const hpPercent = tower.hp / tower.maxHp;
    const hpBarWidth = this.PANEL_WIDTH - 24;
    const hpBarFillWidth = hpBarWidth * hpPercent;
    
    this.hpBarFill.clear();
    if (hpPercent > 0.6) {
      this.hpBarFill.beginFill(0x00ff00, 0.9); // Green
    } else if (hpPercent > 0.3) {
      this.hpBarFill.beginFill(0xffff00, 0.9); // Yellow
    } else {
      this.hpBarFill.beginFill(0xff0000, 0.9); // Red
    }
    this.hpBarFill.drawRect(12, this.HEADER_HEIGHT + 10, hpBarFillWidth, 20);
    this.hpBarFill.endFill();
    
    this.hpText.text = `${Math.ceil(tower.hp)}/${tower.maxHp}`;

    // Stats (more compact - combine info)
    const description = towerDescriptions[tower.type] || 'Tower';
    let statsText = `${description} | DMG: ${tower.damage} | RNG: ${tower.range} | SPD: ${tower.attackSpeed.toFixed(1)}/s`;
    
    if (tower.type === 'cannon' && tower.aoeRadius) {
      statsText += ` | AoE: ${tower.aoeRadius}`;
    }
    
    if (tower.type === 'arrow' && tower.level === 'B') {
      const currentTime = Date.now() / 1000;
      if (tower.lastAbilityTime === undefined || tower.lastAbilityTime === -1) {
        statsText += `\n⚡ Rapid Fire: Ready!`;
      } else {
        const timeSinceLastAbility = currentTime - tower.lastAbilityTime;
        if (tower.abilityActive) {
          const remaining = Math.max(0, 6 - timeSinceLastAbility);
          statsText += `\n⚡ RAPID FIRE: ${remaining.toFixed(1)}s`;
        } else {
          const cooldown = Math.max(0, 14 - timeSinceLastAbility);
          if (cooldown > 0) {
            statsText += `\n⚡ Cooldown: ${cooldown.toFixed(1)}s`;
          } else {
            statsText += `\n⚡ Ready!`;
          }
        }
      }
    }
    
    if (isBuildMode && tower.type === 'sniper') {
      statsText += `\nTrue Strike: Ignores evasion`;
    }
    
    this.statsText.text = statsText;

    // Hide upgrade buttons in build mode
    if (isBuildMode) {
      this.upgradeAButton.visible = false;
      this.upgradeBButton.visible = false;
      this.upgradeCButton.visible = false;
      this.upgradeAText.visible = false;
      this.upgradeBText.visible = false;
      this.upgradeCText.visible = false;
      this.sellButton.visible = false;
      this.sellText.visible = false;
      this.repairButton.visible = false;
      this.repairText.visible = false;
    } else {
      this.sellButton.visible = true;
      this.sellText.visible = true;
      
      // Calculate sell value
      const baseCost = TOWER_STATS[tower.type].cost;
      let totalCost = baseCost;
      if (tower.hasUpgradeA) {
        totalCost += UPGRADE_A_STATS[tower.type].cost;
      }
      if (tower.hasUpgradeB) {
        totalCost += UPGRADE_B_STATS[tower.type].cost;
      }
      if (tower.hasUpgradeC && (tower.type === 'arrow' || tower.type === 'cannon' || tower.type === 'sniper')) {
        totalCost += UPGRADE_C_STATS[tower.type].cost;
      }
      
      let sellValue = Math.floor(totalCost * 0.5);
      if (tower.hp < tower.maxHp) {
        sellValue = Math.floor(sellValue * 0.4);
      }
      
      const actionButtonWidth = (this.PANEL_WIDTH - 30 - this.BUTTON_SPACING) / 2;
      this.sellButton.clear();
      this.sellButton.beginFill(0x660000, 0.9);
      this.sellButton.drawRect(0, 0, actionButtonWidth, this.BUTTON_HEIGHT);
      this.sellButton.endFill();
      this.sellButton.lineStyle(2, 0x990000, 0.8);
      this.sellButton.drawRect(0, 0, actionButtonWidth, this.BUTTON_HEIGHT);
      this.sellText.text = `SELL\n${sellValue}g`;

      // Repair button
      if (tower.hp < tower.maxHp) {
        this.repairButton.visible = true;
        this.repairText.visible = true;
        const isRepairing = this.repairSystem?.isRepairing(tower.id) ?? false;
        if (isRepairing) {
          const progress = this.repairSystem?.getRepairProgress(tower.id) ?? 0;
          this.repairButton.clear();
          this.repairButton.beginFill(0x006666, 0.9);
          this.repairButton.drawRect(0, 0, actionButtonWidth, this.BUTTON_HEIGHT);
          this.repairButton.endFill();
          this.repairButton.lineStyle(2, 0x009999, 0.8);
          this.repairButton.drawRect(0, 0, actionButtonWidth, this.BUTTON_HEIGHT);
          this.repairText.text = `REPAIRING\n${Math.round(progress * 100)}%`;
        } else {
          const missingHpPercent = (tower.maxHp - tower.hp) / tower.maxHp;
          const costPercent = Math.min(0.45, Math.ceil(missingHpPercent * 10) * 0.05);
          const repairCost = Math.floor(totalCost * costPercent);
          const duration = Math.max(0.1, missingHpPercent * 10);
          this.repairButton.clear();
          this.repairButton.beginFill(0x006600, 0.9);
          this.repairButton.drawRect(0, 0, actionButtonWidth, this.BUTTON_HEIGHT);
          this.repairButton.endFill();
          this.repairButton.lineStyle(2, 0x00aa00, 0.8);
          this.repairButton.drawRect(0, 0, actionButtonWidth, this.BUTTON_HEIGHT);
          this.repairText.text = `REPAIR\n${repairCost}g\n${duration.toFixed(1)}s`;
        }
      } else {
        this.repairButton.visible = false;
        this.repairText.visible = false;
      }
      
      // Upgrade buttons
      const upgradeButtonWidth = (this.PANEL_WIDTH - 24 - this.BUTTON_SPACING) / 2;

    // Upgrade A
      if (!tower.hasUpgradeA) {
      const cost = getUpgradeCost(tower.type, 'A');
        const color = canAffordA ? 0x0088ff : 0x660000;
        const borderColor = canAffordA ? 0x00aaff : 0x990000;
      this.upgradeAButton.clear();
        this.upgradeAButton.beginFill(color, 0.9);
        this.upgradeAButton.drawRect(0, 0, upgradeButtonWidth, this.BUTTON_HEIGHT);
      this.upgradeAButton.endFill();
        this.upgradeAButton.lineStyle(2, borderColor, 0.8);
        this.upgradeAButton.drawRect(0, 0, upgradeButtonWidth, this.BUTTON_HEIGHT);
          this.upgradeAText.text = `UPGRADE A\n${cost}g`;
        this.upgradeAState = canAffordA ? 'available' : 'unavailable';
    } else {
      this.upgradeAButton.clear();
        this.upgradeAButton.beginFill(0x00ff00, 0.9);
        this.upgradeAButton.drawRect(0, 0, upgradeButtonWidth, this.BUTTON_HEIGHT);
      this.upgradeAButton.endFill();
        this.upgradeAButton.lineStyle(2, 0x00cc00, 0.8);
        this.upgradeAButton.drawRect(0, 0, upgradeButtonWidth, this.BUTTON_HEIGHT);
        this.upgradeAText.text = `UPGRADE A\n✓ OWNED`;
        this.upgradeAState = 'disabled';
    }
      this.upgradeAButton.visible = true;
      this.upgradeAText.visible = true;

    // Upgrade B
      if (tower.hasUpgradeA && !tower.hasUpgradeB) {
      const cost = getUpgradeCost(tower.type, 'B');
        const color = canAffordB ? 0x0088ff : 0x660000;
        const borderColor = canAffordB ? 0x00aaff : 0x990000;
        this.upgradeBButton.clear();
        this.upgradeBButton.beginFill(color, 0.9);
        this.upgradeBButton.drawRect(0, 0, upgradeButtonWidth, this.BUTTON_HEIGHT);
        this.upgradeBButton.endFill();
        this.upgradeBButton.lineStyle(2, borderColor, 0.8);
        this.upgradeBButton.drawRect(0, 0, upgradeButtonWidth, this.BUTTON_HEIGHT);
        this.upgradeBText.text = `UPGRADE B\n${cost}g`;
        this.upgradeBState = canAffordB ? 'available' : 'unavailable';
      } else if (tower.hasUpgradeB) {
      this.upgradeBButton.clear();
        this.upgradeBButton.beginFill(0x00ff00, 0.9);
        this.upgradeBButton.drawRect(0, 0, upgradeButtonWidth, this.BUTTON_HEIGHT);
      this.upgradeBButton.endFill();
        this.upgradeBButton.lineStyle(2, 0x00cc00, 0.8);
        this.upgradeBButton.drawRect(0, 0, upgradeButtonWidth, this.BUTTON_HEIGHT);
        this.upgradeBText.text = `UPGRADE B\n✓ OWNED`;
        this.upgradeBState = 'disabled';
    } else {
      this.upgradeBButton.clear();
        this.upgradeBButton.beginFill(0x333333, 0.9);
        this.upgradeBButton.drawRect(0, 0, upgradeButtonWidth, this.BUTTON_HEIGHT);
      this.upgradeBButton.endFill();
        this.upgradeBButton.lineStyle(2, 0x555555, 0.8);
        this.upgradeBButton.drawRect(0, 0, upgradeButtonWidth, this.BUTTON_HEIGHT);
        this.upgradeBText.text = `UPGRADE B\nLOCKED\nRequires A first`;
        this.upgradeBState = 'locked';
      }
      this.upgradeBButton.visible = true;
      this.upgradeBText.visible = true;

      // Upgrade C (only for physical towers)
      if (upgradeC && (tower.type === 'arrow' || tower.type === 'cannon' || tower.type === 'sniper')) {
        if (tower.hasUpgradeB && !tower.hasUpgradeC) {
          const cost = getUpgradeCost(tower.type, 'C');
          const color = canAffordC ? 0xff00ff : 0x660000; // Magenta for C
          const borderColor = canAffordC ? 0xff44ff : 0x990000;
          this.upgradeCButton.clear();
          this.upgradeCButton.beginFill(color, 0.9);
          this.upgradeCButton.drawRect(0, 0, this.PANEL_WIDTH - 24, this.BUTTON_HEIGHT);
          this.upgradeCButton.endFill();
          this.upgradeCButton.lineStyle(2, borderColor, 0.8);
          this.upgradeCButton.drawRect(0, 0, this.PANEL_WIDTH - 24, this.BUTTON_HEIGHT);
          this.upgradeCText.text = `UPGRADE C\n${cost}g`;
          this.upgradeCState = canAffordC ? 'available' : 'unavailable';
        } else if (tower.hasUpgradeC) {
          this.upgradeCButton.clear();
          this.upgradeCButton.beginFill(0x00ff00, 0.9);
          this.upgradeCButton.drawRect(0, 0, this.PANEL_WIDTH - 24, this.BUTTON_HEIGHT);
          this.upgradeCButton.endFill();
          this.upgradeCButton.lineStyle(2, 0x00cc00, 0.8);
          this.upgradeCButton.drawRect(0, 0, this.PANEL_WIDTH - 24, this.BUTTON_HEIGHT);
          this.upgradeCText.text = `UPGRADE C\n✓ OWNED`;
          this.upgradeCState = 'disabled';
        } else {
          this.upgradeCButton.clear();
          this.upgradeCButton.beginFill(0x333333, 0.9);
          this.upgradeCButton.drawRect(0, 0, this.PANEL_WIDTH - 24, this.BUTTON_HEIGHT);
          this.upgradeCButton.endFill();
          this.upgradeCButton.lineStyle(2, 0x555555, 0.8);
          this.upgradeCButton.drawRect(0, 0, this.PANEL_WIDTH - 24, this.BUTTON_HEIGHT);
          this.upgradeCText.text = `UPGRADE C\nLOCKED\nRequires B first`;
          this.upgradeCState = 'locked';
        }
        this.upgradeCButton.visible = true;
        this.upgradeCText.visible = true;
      } else {
        this.upgradeCButton.visible = false;
        this.upgradeCText.visible = false;
      }
    }
  }

  refresh(tower: Tower, canAffordA: boolean, canAffordB: boolean, canAffordC: boolean = false, isBuildMode: boolean = false): void {
    if (this.tower && this.tower.id === tower.id) {
      this.update(tower, canAffordA, canAffordB, canAffordC, isBuildMode);
    }
  }

  refreshWall(canAfford: boolean, wall?: Wall): void {
    if (this.wall && wall && this.wall.id === wall.id) {
      this.updateWall(canAfford, wall);
    }
  }

  private updateWall(canAfford: boolean, wall?: Wall): void {
    // Hide upgrade buttons for walls
    this.upgradeAButton.visible = false;
    this.upgradeBButton.visible = false;
    this.upgradeAText.visible = false;
    this.upgradeBText.visible = false;

    // Header
    this.nameText.text = 'WALL';
    this.levelText.text = '';

    // HP Bar
    if (wall) {
      const hpPercent = wall.hp / wall.maxHp;
      const hpBarWidth = this.PANEL_WIDTH - 24;
      const hpBarFillWidth = hpBarWidth * hpPercent;
      
      this.hpBarFill.clear();
      if (hpPercent > 0.6) {
        this.hpBarFill.beginFill(0x00ff00, 0.9);
      } else if (hpPercent > 0.3) {
        this.hpBarFill.beginFill(0xffff00, 0.9);
      } else {
        this.hpBarFill.beginFill(0xff0000, 0.9);
      }
      this.hpBarFill.drawRect(12, this.HEADER_HEIGHT + 10, hpBarFillWidth, 20);
      this.hpBarFill.endFill();
      
      this.hpText.text = `${Math.ceil(wall.hp)}/${wall.maxHp}`;
      
      this.statsText.text = `Blocks enemy path | HP: ${wall.hp}/${wall.maxHp}`;
    } else {
      this.hpBarFill.clear();
      this.hpText.text = `${GAME_CONSTANTS.WALL_HP_BASE}/${GAME_CONSTANTS.WALL_HP_BASE}`;
      const costText = canAfford ? `${GAME_CONSTANTS.WALL_COST}g` : `${GAME_CONSTANTS.WALL_COST}g (Can't afford)`;
      this.statsText.text = `Cost: ${costText} | HP: ${GAME_CONSTANTS.WALL_HP_BASE} | Blocks path`;
      this.statsText.style.fill = canAfford ? 0xcccccc : 0xff6666;
    }

    // Action buttons
    const actionButtonWidth = (this.PANEL_WIDTH - 24 - this.BUTTON_SPACING) / 2;
    const actionY = this.HEADER_HEIGHT + this.STATS_HEIGHT + 8 + this.BUTTON_HEIGHT + this.BUTTON_SPACING;

    if (wall) {
      this.sellButton.visible = true;
      this.sellText.visible = true;
      
      let sellValue = Math.floor(GAME_CONSTANTS.WALL_COST * 0.5);
      if (wall.hp < wall.maxHp) {
        sellValue = Math.floor(sellValue * 0.4);
      }
      
      this.sellButton.clear();
      this.sellButton.beginFill(0x660000, 0.9);
      this.sellButton.drawRect(0, 0, actionButtonWidth, this.BUTTON_HEIGHT);
      this.sellButton.endFill();
      this.sellButton.lineStyle(2, 0x990000, 0.8);
      this.sellButton.drawRect(0, 0, actionButtonWidth, this.BUTTON_HEIGHT);
      this.sellText.text = `SELL\n${sellValue}g`;

      if (wall.hp < wall.maxHp) {
        this.repairButton.visible = true;
        this.repairText.visible = true;
        const isRepairing = this.repairSystem?.isRepairing(wall.id) ?? false;
        if (isRepairing) {
          const progress = this.repairSystem?.getRepairProgress(wall.id) ?? 0;
          this.repairButton.clear();
          this.repairButton.beginFill(0x006666, 0.9);
          this.repairButton.drawRect(0, 0, actionButtonWidth, this.BUTTON_HEIGHT);
          this.repairButton.endFill();
          this.repairButton.lineStyle(2, 0x009999, 0.8);
          this.repairButton.drawRect(0, 0, actionButtonWidth, this.BUTTON_HEIGHT);
          this.repairText.text = `REPAIRING\n${Math.round(progress * 100)}%`;
        } else {
          const missingHpPercent = (wall.maxHp - wall.hp) / wall.maxHp;
          const costPercent = Math.min(0.45, Math.ceil(missingHpPercent * 10) * 0.05);
          const repairCost = Math.floor(GAME_CONSTANTS.WALL_COST * costPercent);
          const duration = Math.max(0.1, missingHpPercent * 10);
          this.repairButton.clear();
          this.repairButton.beginFill(0x006600, 0.9);
          this.repairButton.drawRect(0, 0, actionButtonWidth, this.BUTTON_HEIGHT);
          this.repairButton.endFill();
          this.repairButton.lineStyle(2, 0x00aa00, 0.8);
          this.repairButton.drawRect(0, 0, actionButtonWidth, this.BUTTON_HEIGHT);
          this.repairText.text = `REPAIR\n${repairCost}g\n${duration.toFixed(1)}s`;
        }
      } else {
        this.repairButton.visible = false;
        this.repairText.visible = false;
      }
    } else {
      this.sellButton.visible = false;
      this.sellText.visible = false;
      this.repairButton.visible = false;
      this.repairText.visible = false;
    }
  }

  getContainer(): PIXI.Container {
    return this.container;
  }
}
